import React, { useRef, useState } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import type { MapRef } from 'react-map-gl';
import ReactMapGl, {
  GeolocateControl,
  NavigationControl,
  ScaleControl,
  AttributionControl,
} from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '@/app/store';
import { Snackbars } from '@/components/Elements/Snackbar/Snackbar';
import {
  changeIsLoadedOverlay,
  changeZoom,
  changeCurrentLocation,
  changeCentralCoordinateLocationPermitted,
} from '@/features/mapbox/mapSlice';

export const ErrorFallback = ({ error }: FallbackProps) => {
  const dispatch = useDispatch();

  const mapRef = useRef<MapRef>(null);
  const { serviceProfile } = useSelector(
    (state: RootState) => state.serviceProfile
  );
  const mapData = useSelector((state: RootState) => state.map);
  const [open, setOpen] = React.useState(true);

  const mapConfig = {
    maxZoom: serviceProfile.mapConfig.maxZoom,
    minZoom: serviceProfile.mapConfig.minZoom,
    mapStyle: serviceProfile.mapConfig.styleUrl,
    mapboxAccessToken: serviceProfile.mapConfig.accessToken,
  };

  const [viewState, setViewState] = useState({
    longitude: mapData.currentLocation.longitude,
    latitude: mapData.currentLocation.latitude,
    zoom: mapData.zoom,
    bearing: mapData.bearing,
    pitch: mapData.pitch,
  });
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };
  console.error(error.message);

  return (
    <div
      id="hazard-map-app"
      className="absolute left-0 top-0 min-h-screen w-full overflow-hidden"
    >
      {mapData.isEnabledMap && (
        <ReactMapGl
          ref={mapRef}
          {...viewState}
          {...mapConfig}
          attributionControl={false}
          style={{
            width: '100vw',
            height: '100%',
            minHeight: '100%',
            position: 'absolute',
            top: '0',
            left: '0',
          }}
          onMove={(evt) => {
            setViewState(() => evt.viewState);
            dispatch(
              changeCurrentLocation({
                latitude: evt.viewState.latitude,
                longitude: evt.viewState.longitude,
              })
            );
            dispatch(
              changeCentralCoordinateLocationPermitted({
                latitude: evt.viewState.latitude,
                longitude: evt.viewState.longitude,
              })
            );
          }}
          onIdle={() => {
            dispatch(changeIsLoadedOverlay(true));
          }}
          onZoom={(evt) => {
            dispatch(changeZoom(evt.target.getZoom()));
          }}
        >
          <AttributionControl position="top-right" />
          {Boolean(serviceProfile.mapConfig.view.currentPositionButton) && (
            <GeolocateControl />
          )}
          {Boolean(serviceProfile.mapConfig.view.zoomInOutButton) && (
            <NavigationControl
              showCompass={serviceProfile.mapConfig.view.bearingButton}
            />
          )}
          {Boolean(serviceProfile.mapConfig.view.scale) && (
            <ScaleControl position="top-right" unit="metric" maxWidth={60} />
          )}
          <Snackbars
            isOpen={open}
            handleClose={handleClose}
            message={'不明なエラーが発生しました'}
            severity={'error'}
          />
        </ReactMapGl>
      )}
    </div>
  );
};
